/**
 * Quiet music that plays under everything, and never repeats.
 *
 * There are good freely-licensed tracks out there, but a recorded loop is the
 * wrong shape for this app for three reasons: it is megabytes on a page that
 * currently ships none, it loops — and a child hears the seam by the third
 * pass — and it is in some key that has nothing to do with the sounds the toys
 * make, so the app would spend all day arguing with its own soundtrack.
 *
 * So the music is generated, from the same pentatonic scale as everything
 * else. Notes are chosen by a slow random walk up and down the scale, held
 * long, and overlapped, which is how ambient music is actually built. It costs
 * no bytes, has no licence, never repeats, and cannot clash with a single
 * sound in the app, because it is made of the same five notes.
 */

import { noteAt, playNote } from './audio.ts';

/** Seconds between notes. Slow: this is furniture, not a tune. */
const BEAT = 2.6;
/** How far the walk can wander from the root, in scale degrees. */
const RANGE = 7;

let timer: ReturnType<typeof setInterval> | null = null;
let degree = 0;
let step = 0;

/** Where the next note goes: mostly nearby, occasionally a leap. */
function wander(from: number): number {
  const roll = Math.random();
  const move = roll < 0.42 ? 1 : roll < 0.84 ? -1 : roll < 0.94 ? 2 : -2;
  const next = from + move;
  // Turn back rather than clamping, so it does not stick at the edges.
  if (next > RANGE) return from - 1;
  if (next < -RANGE) return from + 1;
  return next;
}

function voice(): void {
  degree = wander(degree);
  step++;

  // The melody note, soft and long.
  playNote(degree, { type: 'sine', gain: 0.055, duration: 3.4 });

  // A fifth below every few notes, which is what gives it a floor to sit on.
  if (step % 3 === 0) playNote(degree - 7, { type: 'triangle', gain: 0.035, duration: 5.2, delay: 0.4 });

  // Once in a while a note an octave up, arriving late, like a bell in another
  // room. Rare on purpose: predictable sparkle stops being sparkle.
  if (Math.random() < 0.22) playNote(degree + 5, { type: 'sine', gain: 0.03, duration: 2.6, delay: 1.1 });
}

const STORAGE_KEY = 'imagineplay.music';

export function isAmbientPlaying(): boolean {
  return timer !== null;
}

/**
 * Off until asked for. Music that starts itself is an imposition — and every
 * browser blocks it before the first tap anyway, so a default of "on" would
 * only mean it starts at some surprising later moment.
 */
export function ambientWanted(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(STORAGE_KEY) === 'on';
}

export function setAmbient(on: boolean): void {
  if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, on ? 'on' : 'off');
  if (on) startAmbient();
  else stopAmbient();
}

export function startAmbient(): void {
  if (timer !== null || typeof setInterval === 'undefined') return;
  voice();
  timer = setInterval(voice, BEAT * 1000);
}

export function stopAmbient(): void {
  if (timer === null) return;
  clearInterval(timer);
  timer = null;
}

/** For the tests: the frequency the walk is currently sounding. */
export function currentFrequency(): number {
  return noteAt(degree);
}

/** For the tests: run the walk without a Web Audio context. */
export function walkFrom(start: number, steps: number): number[] {
  const out: number[] = [];
  let at = start;
  for (let i = 0; i < steps; i++) {
    at = wander(at);
    out.push(at);
  }
  return out;
}
